import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cote-azur/chauffeur-cote-azur.png";

const saintJeanCapFerrat: ChauffeurPage = {
  slug: "saint-jean-cap-ferrat",
  title: "VTC & chauffeur privé à Saint-Jean-Cap-Ferrat",
  subtitle:
    "Chauffeur privé à Saint-Jean-Cap-Ferrat : villas et palaces de la presqu'île, accueil aéroport et discrétion absolue.",
  keywords: [
    "vtc saint-jean-cap-ferrat",
    "chauffeur privé saint-jean-cap-ferrat",
    "chauffeur cap-ferrat",
  ],
  hero: {
    title: "VTC & chauffeur privé à Saint-Jean-Cap-Ferrat",
    subtitle:
      "La presqu'île la plus confidentielle de la Riviera, entre Nice et Monaco : un chauffeur pour villas et palaces.",
    keywords: [
      "VTC Saint-Jean-Cap-Ferrat",
      "chauffeur Cap-Ferrat",
      "chauffeur villa presqu'île",
    ],
    imageSrc: heroImage.src,
    imageAlt: "VTC & chauffeur privé à Saint-Jean-Cap-Ferrat",
    badges: ["Accueil aéroport Nice", "Dépose villa", "Discrétion UHNW"],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à Saint-Jean-Cap-Ferrat",
    items: [
      {
        title: "Discrétion pour une clientèle privée",
        description:
          "Presqu'île de villas et de résidences fortunées : dépose à la grille, sans manœuvre ni hésitation, en coordination conciergerie.",
      },
      {
        title: "Palaces de la presqu'île",
        description:
          "Grand-Hôtel du Cap-Ferrat (Four Seasons), Royal-Riviera, La Voile d'Or : accueil et reprise au pied de l'hôtel.",
      },
      {
        title: "Aéroport tout proche",
        description:
          "Liaison rapide avec Nice Côte d'Azur, environ 20 à 30 minutes par la basse corniche, accueil pancarte aux deux terminaux.",
      },
      {
        title: "Entre Nice et Monaco",
        description:
          "Position idéale pour rejoindre la Principauté, Èze ou le Vieux-Nice sans contrainte de stationnement sur la presqu'île.",
      },
    ],
  },
  services: {
    heading: "Services chauffeur à Saint-Jean-Cap-Ferrat",
    services: [
      {
        title: "Transferts aéroport",
        text: "Saint-Jean-Cap-Ferrat ↔ aéroport de Nice avec suivi de vol et accueil pancarte, pour arrivées d'invités et départs en jet.",
      },
      {
        title: "Dépose villa & palace",
        text: "Prise en charge à la grille de la villa ou au pied du Grand-Hôtel du Cap-Ferrat, coordination avec gardiens et conciergeries.",
      },
      {
        title: "Mise à disposition",
        text: "Chauffeur dédié pour propriétaires et familles, de la Villa Ephrussi de Rothschild aux dîners de Monaco ou Beaulieu.",
      },
      {
        title: "Liaisons Riviera",
        text: "Monaco, Èze, Villefranche-sur-Mer et Nice : trajets directs ou journée complète depuis la presqu'île.",
      },
      {
        title: "Plage de Paloma & sentier littoral",
        text: "Dépose à la Plage de Paloma et reprise après la balade du sentier du littoral, sans souci de parking en saison.",
      },
    ],
  },
  locations: {
    heading: "Lieux & points de prise en charge",
    items: [
      {
        name: "Villa Ephrussi de Rothschild",
        description:
          "Villa-musée et jardins, l'un des sites phares de la presqu'île.",
      },
      {
        name: "Grand-Hôtel du Cap-Ferrat",
        description:
          "Palace Four Seasons, dépose et reprise au pied de l'hôtel.",
      },
      {
        name: "Plage de Paloma",
        description:
          "Crique prisée à l'est de la presqu'île, accès au sentier littoral.",
      },
      {
        name: "Le Port & le village",
        description: "Port de plaisance et restaurants du bourg de Saint-Jean.",
      },
      {
        name: "Villas du Cap",
        description:
          "Routes privées et confidentielles, dépose discrète à la grille.",
      },
      {
        name: "Beaulieu-sur-Mer voisin",
        description: "Gare et front de mer attenants à la presqu'île.",
      },
    ],
  },
  generic: {
    heading: "Saint-Jean-Cap-Ferrat, la presqu'île des grandes villas",
    subheading:
      "Entre Nice et Monaco, un cap discret où le chauffeur fait toute la différence.",
    paragraphs: [
      "Saint-Jean-Cap-Ferrat aligne sur ses routes étroites quelques-unes des villas les plus prisées de la Côte d'Azur, de la Villa Ephrussi de Rothschild aux propriétés privées du Cap. Un chauffeur qui en connaît les accès dépose directement à la grille, en toute discrétion et en coordination avec gardiens et conciergeries.",
      "La presqu'île n'est qu'à une vingtaine de minutes de l'aéroport de Nice par la basse corniche. Pour une arrivée d'invités, un départ en jet ou un dîner à Monaco, le même chauffeur enchaîne les trajets sans contrainte de stationnement, particulièrement sensible en haute saison sur le Cap.",
      "Pour une journée sur place — Villa Ephrussi, Plage de Paloma, balade du sentier du littoral puis dîner au Grand-Hôtel du Cap-Ferrat — la mise à disposition garde le véhicule à votre main d'un point à l'autre.",
    ],
    bullets: [
      "Suivi de vol T1/T2",
      "Dépose à la grille",
      "Coordination conciergerie",
    ],
  },
  process: {
    heading: "Réserver un chauffeur à Saint-Jean-Cap-Ferrat",
    steps: [
      "Indiquez le point de prise en charge (villa, palace, aéroport, gare de Beaulieu).",
      "Nous calons l'heure selon le vol, l'arrivée des invités ou votre programme.",
      "Le chauffeur se présente à la grille ou au pied de l'hôtel, en toute discrétion.",
      "Transfert direct ou mise à disposition pour la journée sur la presqu'île et la Riviera.",
    ],
  },
  faq: [
    {
      q: "Combien de temps de l'aéroport de Nice à Saint-Jean-Cap-Ferrat ?",
      a: "Comptez environ 20 à 30 minutes par la basse corniche, hors heures de pointe. Nous suivons le vol des invités pour ajuster la prise en charge et prévoyons une marge en saison.",
    },
    {
      q: "Desservez-vous les villas privées du Cap-Ferrat ?",
      a: "Oui. Nous assurons la dépose et la reprise directement à la grille de la villa, en toute discrétion et en coordination avec les gardiens ou la conciergerie si besoin.",
    },
    {
      q: "Pouvez-vous prendre en charge au Grand-Hôtel du Cap-Ferrat ?",
      a: "Oui, nous accueillons et reprenons au pied du Grand-Hôtel du Cap-Ferrat comme des autres établissements de la presqu'île (Royal-Riviera, La Voile d'Or), en lien avec la conciergerie.",
    },
    {
      q: "Le stationnement est-il un problème sur la presqu'île en été ?",
      a: "Les abords de la Plage de Paloma et du village se saturent vite en saison. Avec un chauffeur, vous êtes déposé au plus près et repris à la demande, sans recherche de place.",
    },
  ],
  relatedLinks: [
    {
      label: "Liaison Aéroport de Nice ⇄ Saint-Jean-Cap-Ferrat",
      href: "/chauffeur/transferts/aeroport-nice-saint-jean-cap-ferrat",
    },
    {
      label: "Liaison Monaco ⇄ Aéroport de Nice",
      href: "/chauffeur/transferts/monaco-aeroport-nice",
    },
    {
      label: "Trouver un transfert",
      href: "/chauffeur/transferts",
    },
  ],
  cta: {
    title: "Votre chauffeur privé à Saint-Jean-Cap-Ferrat",
    text: "Villa au Cap, palace ou arrivée à l'aéroport de Nice : réservez votre chauffeur en toute discrétion.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver un chauffeur",
  },
  seo: {
    title:
      "VTC Saint-Jean-Cap-Ferrat — chauffeur privé villas & palaces | KS Rent Services",
    description:
      "VTC et chauffeur privé à Saint-Jean-Cap-Ferrat : villas du Cap, Grand-Hôtel du Cap-Ferrat, Villa Ephrussi et transfert aéroport de Nice. Discrétion, 24/7.",
    canonical: "/chauffeur/saint-jean-cap-ferrat",
  },
};

export default saintJeanCapFerrat;
